import { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  parseTransactionParams,
  safeBackPath,
  toSearchParams,
  type TransactionLinkFilters,
} from '@/lib/transactionsLink'
import { currentMonth } from '@/lib/transactionDateRangeFilter'

export function hasPeriod(f: TransactionLinkFilters): boolean {
  return !!(f.month || f.date || f.dateFrom || f.dateTo || f.spendMonthFrom)
}

/** Every period-free view (e.g. a link with only an account) opens on this month. */
export function withDefaultPeriod(f: TransactionLinkFilters): TransactionLinkFilters {
  return hasPeriod(f) ? f : { ...f, month: currentMonth() }
}

/**
 * The Transactions page's filters, kept in the URL. Changes replace the history entry so Back
 * leaves the page instead of stepping through every filter tweak, while refresh, bookmarks and
 * shared links reopen the same view.
 */
export function useTransactionFilters() {
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => withDefaultPeriod(parseTransactionParams(params)), [params])
  const back = safeBackPath(params.get('back'))

  const setFilters = useCallback(
    (next: TransactionLinkFilters) => setParams(toSearchParams(next, back), { replace: true }),
    [setParams, back]
  )

  const update = useCallback(
    (patch: Partial<TransactionLinkFilters>) => {
      const next = { ...filters, ...patch }
      for (const key of Object.keys(next) as (keyof TransactionLinkFilters)[]) {
        if (next[key] === undefined || next[key] === '' || next[key] === false) delete next[key]
      }
      setFilters(next)
    },
    [filters, setFilters]
  )

  const clear = useCallback(() => setFilters({ month: currentMonth() }), [setFilters])

  return { filters, back, setFilters, update, clear }
}

export type FilterMode = 'short' | 'full'
const MODE_KEY = 'fl.transactions.filterMode'

/** Remembered per device; storage can be blocked (private mode), so it is a convenience only. */
export function useFilterMode() {
  const [mode, setModeState] = useState<FilterMode>(() => {
    try {
      return localStorage.getItem(MODE_KEY) === 'full' ? 'full' : 'short'
    } catch {
      return 'short'
    }
  })
  const setMode = useCallback((next: FilterMode) => {
    setModeState(next)
    try {
      localStorage.setItem(MODE_KEY, next)
    } catch {
      // Not persisted; the choice still holds for this visit.
    }
  }, [])
  return { mode, setMode }
}
